import React from 'react';
import styled from '@emotion/styled';
import config from 'config';
import LogoLink from 'components/cards/new-layout/components/LogoLink';
import PropertyImage from 'components/cards/new-layout/components/PropertyImage';
import { IThumbnail, PriceEvent, PoiId, DealType } from 'utils/entities';
import ListingTags from 'components/cards/new-layout/components/ListingTags';
import ProjectTag from 'components/cards/new-layout/components/ProjectTag';
import { ISOString } from 'locale/formatDate';
import { isPriceDropped } from 'utils/priceEvents';
import { CardType } from 'components/cards/types';
import Tag, { TagType } from '../components/Tag';
import { useLocale } from 'locale';
import { RenewalProgramStatus } from 'components/real-estate-listing-card/RenewalProgramIndicator';
import Ur0Icon from 'assets/svg/urban-renewal/ur-0-v3.svg';
import Ur1Icon from 'assets/svg/urban-renewal/ur-1-v3.svg';
import Ur2Icon from 'assets/svg/urban-renewal/ur-2-v3.svg';
import Ur3Icon from 'assets/svg/urban-renewal/ur-3-v3.svg';
import Ur4Icon from 'assets/svg/urban-renewal/ur-4-v3.svg';
import Ur5Icon from 'assets/svg/urban-renewal/ur-5-v3.svg';

const UR_STATUS_ICONS = [ Ur0Icon, Ur1Icon, Ur2Icon, Ur3Icon, Ur4Icon, Ur5Icon ];


const CardImageWithTagsWrapper = styled.div`
  position: relative;
`;

const ThumbnailLogoWrapper = styled.div<{ small: boolean; }>`
  ${({ small, theme }) => `
    background-color: ${theme.colors.neutrals.white};
    border-radius: ${theme.borderRadius.tiny};
    position: absolute;
    ${small ? `
      right: 8px;
      bottom: 8px;
    ` : `
      right: 4px;
      bottom: 4px;
    `}
  `}
`;

const TagsWrapper = styled.div<{ small: boolean; }>`
  ${({ small }) => `
    position: absolute;
    ${small ? `
      top: 8px;
      right: 8px;
    ` : `
      top: 4px;
      right: 4px;
    `}
  `}
`;

const URTagsWrapper = styled(TagsWrapper)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

interface CardImageWithTagsProps {
  cardType: CardType;
  small: boolean;
  logoPath: string;
  thumbnails: IThumbnail[];
  logoSourceId: string;
  eventsHistory: PriceEvent[];
  hasDiscount: boolean;
  isPromoted: boolean;
  id: PoiId;
  date: ISOString;
  lastUpdatedDate: ISOString;
  dealType: DealType;
  showSchoolInAreaLabel: boolean;
  isURPotential?: boolean;
  urbanRenewalStatus?: number;
  isMapPreview?: boolean;
}


const CardImageWithTags: React.FC<CardImageWithTagsProps> = (props) => {
  const { t } = useLocale();
  const {
    logoPath,
    cardType,
    thumbnails,
    small,
    showSchoolInAreaLabel,
    isPromoted,
    hasDiscount,
    id,
    eventsHistory,
    dealType,
    lastUpdatedDate,
    date,
    logoSourceId,
    isURPotential,
    urbanRenewalStatus,
    isMapPreview,
  } = props;

  const isProject = props.cardType === CardType.Project || props.cardType === CardType.CommercialProject;
  const withLogoPreview = Boolean(config.universalCardWithLogo && props.logoPath);

  let tags = null;

  if (isProject) {
    tags = <ProjectTag id={id} inSearchArea={showSchoolInAreaLabel} hasDiscount={hasDiscount} isPromoted={isPromoted} />;
  }
  else {
    tags = (
      <ListingTags
        inSearchArea={showSchoolInAreaLabel}
        hasPriceDrop={isPriceDropped(eventsHistory)}
        lastUpdatedDate={lastUpdatedDate}
        createdDate={date}
        dealType={dealType}
        id={id}
      />
    );
  }

  return (
    <div>
      <CardImageWithTagsWrapper>
        {withLogoPreview ? (
          <ThumbnailLogoWrapper className="thumbnail-logo" small={small}>
            <LogoLink size={40} logoPath={logoPath} cardType={cardType} id={logoSourceId} />
          </ThumbnailLogoWrapper>
        ) : null}
        {isURPotential ? (
          <URTagsWrapper small={small}>
            {isMapPreview ? (() => {
              const URIcon = UR_STATUS_ICONS[urbanRenewalStatus];
              return URIcon ? <URIcon width={24} height={24} /> : null;
            })() : (
              <Tag
                data-auto="date-label"
                type={TagType.URPotential}
                label={urbanRenewalStatus === RenewalProgramStatus.PermitGranted ? t('card.tag.ur') : undefined}
              />
            )}
            {tags}
          </URTagsWrapper>
        ) : (
          <TagsWrapper small={small}>
            {tags}
          </TagsWrapper>
        )}
        <PropertyImage layout={small ? 'vertical' : 'horizontal'} thumbnails={thumbnails} />
      </CardImageWithTagsWrapper>
    </div>
  );
};

export default CardImageWithTags;
